/**
 * Cart interaction — the drawer and the cart page.
 *
 * `assets/cart.js` is the data layer: it talks to the Cart AJAX API and publishes
 * `hubcart:cart:updated` on `document`. This file is the only thing that reads that
 * event to touch cart markup — it owns the two custom elements the markup declares
 * (`cart-quantity`, `cart-contents`) and the drawer's own open-on-add behaviour, so a
 * change here can never race a change in `cart.js` for the same DOM node.
 *
 * `cart-contents` wraps exactly the part of the drawer or the page that is replaced on
 * every mutation (see the section comment in `sections/cart-drawer.liquid` for why the
 * panel chrome sits outside it). Both the drawer and the cart page render one, each with
 * its own `data-section-id`; `cart.js`'s section registration is a module-level set, so
 * a mutation from either surface asks Shopify to re-render both and keeps them in sync
 * without either page knowing the other exists.
 */

/**
 * The stepper on one line: a text input plus the two buttons the reference shows at
 * desktop width. It owns nothing about the network — it only ever asks its own value to
 * change and lets the input's `change` event carry that up to `cart-contents`, which is
 * the one place `cart.js` is called from.
 */
class CartQuantity extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-cart-quantity]');
    if (!this.input) return;

    this.buttons = this.querySelectorAll('[data-cart-quantity-step]');
    this.buttons.forEach((button) => {
      button.addEventListener('click', () => this.step(button));
    });

    this.input.addEventListener('change', () => this.commit());

    /* Typing can carry the value past either limit before `change` fires, so the
       buttons stay in sync with every keystroke rather than only after a step. */
    this.input.addEventListener('input', () => this.updateButtons());

    /* Enter commits immediately rather than waiting for the field to blur, which is the
       only way a keyboard shopper gets the same immediacy as a pointer click on the
       stepper. */
    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.input.blur();
      }
    });

    this.updateButtons();
  }

  step(button) {
    const delta = Number(button.dataset.cartQuantityStep);
    const min = Number(this.input.min || 0);
    const max = this.input.max === '' ? Infinity : Number(this.input.max);
    const next = Math.min(max, Math.max(min, Number(this.input.value || 0) + delta));
    if (next === Number(this.input.value)) return;
    this.input.value = next;
    this.updateButtons();
    this.commit();
  }

  /* `component-cart.css` already styles `[disabled]` on this button — this is what was
     missing to ever set it. Without it a click at either limit silently does nothing,
     which reads as broken rather than as "you're already there". */
  updateButtons() {
    const min = Number(this.input.min || 0);
    const max = this.input.max === '' ? Infinity : Number(this.input.max);
    const value = Number(this.input.value || 0);
    this.buttons.forEach((button) => {
      const delta = Number(button.dataset.cartQuantityStep);
      button.disabled = delta > 0 ? value >= max : value <= min;
    });
  }

  commit() {
    const line = Number(this.dataset.line);
    const quantity = Math.max(0, Number(this.input.value || 0));
    if (!line) return;
    this.dispatchEvent(
      new CustomEvent('hubcart:quantity:change', { bubbles: true, detail: { line: line, quantity: quantity } })
    );
  }
}

/**
 * The swapped region: the line list (or the empty state), the note and the totals. One
 * instance lives in the drawer, one on the cart page, and both are driven the same way.
 */
class CartContents extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    if (this.sectionId && window.Hubcart?.cart) window.Hubcart.cart.registerSection(this.sectionId);

    this.noteTimer = null;

    this.addEventListener('hubcart:quantity:change', (event) => {
      const line = this.querySelector(`[data-cart-line="${event.detail.line}"]`);
      this.setLineBusy(line, true);
      this.change(event.detail.line, event.detail.quantity, line);
    });

    this.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-cart-remove]');
      if (!remove) return;
      event.preventDefault();
      const line = remove.closest('[data-cart-line]');
      const index = line ? Number(line.dataset.cartLine) : null;
      if (!index) return;
      line.classList.add('cart-line--removing');
      this.change(index, 0, line);
    });

    this.addEventListener('change', (event) => {
      const note = event.target.closest('[data-cart-note]');
      if (note) this.updateNote(note.value);
    });
  }

  setLineBusy(line, busy) {
    if (line) line.classList.toggle('cart-line--busy', busy);
  }

  setSubtotalBusy(busy) {
    this.querySelectorAll('[data-cart-subtotal]').forEach((el) => el.classList.toggle('is-updating', busy));
  }

  showError(message) {
    const errors = this.querySelectorAll('[data-cart-error]');
    errors.forEach((el) => {
      el.textContent = message || el.dataset.errorFallback || '';
      el.hidden = false;
    });
  }

  hideError() {
    this.querySelectorAll('[data-cart-error]').forEach((el) => {
      el.hidden = true;
    });
  }

  async change(line, quantity, lineEl) {
    this.hideError();
    this.setSubtotalBusy(true);
    try {
      /* `Hubcart.cart.change` already publishes `hubcart:cart:updated` itself — the
         module-level listener below is what re-renders every `cart-contents` on the
         page, including this one, from that single event. */
      await window.Hubcart.cart.change(line, quantity);
    } catch (error) {
      this.setSubtotalBusy(false);
      this.setLineBusy(lineEl, false);
      if (lineEl) lineEl.classList.remove('cart-line--removing');
      this.showError(error?.data?.description || error?.message);
    }
  }

  async updateNote(value) {
    clearTimeout(this.noteTimer);
    this.noteTimer = setTimeout(async () => {
      try {
        await window.Hubcart.cart.updateNote(value);
      } catch {
        this.showError();
      }
    }, 400);
  }

  /* Replaces this element's own contents from the section map a mutation returns.
     Called from the module-level `hubcart:cart:updated` listener below rather than from
     `change()` directly, so a mutation on the *other* surface's `cart-contents` — the
     page updating while the drawer is also on screen — re-renders this one too. */
  render(sections) {
    if (!sections || !this.sectionId || !sections[this.sectionId]) return;
    const doc = new DOMParser().parseFromString(sections[this.sectionId], 'text/html');
    const fresh = doc.querySelector(`[data-section-id="${this.sectionId}"]`);
    if (fresh) this.innerHTML = fresh.innerHTML;
  }

  /* Fallback for a mutation that carried no section map at all — an add from the product
     page or a card's quick-add (see the listener below). Neither registers with `cart.js`
     before it posts, so there is nothing in `sections` to render from; this fetches the
     one section fresh the same way `component-facets.js` and `component-predictive-
     search.js` already do, against the current URL with `section_id` appended, which
     Shopify answers from any page using that request's own cart state. Best-effort: on
     failure the element just keeps showing whatever it last rendered. */
  async refresh() {
    if (!this.sectionId) return;
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', this.sectionId);
      const response = await fetch(url);
      if (!response.ok) return;
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = doc.querySelector(`[data-section-id="${this.sectionId}"]`);
      if (fresh) this.innerHTML = fresh.innerHTML;
    } catch {
      /* Network hiccup: leave the last-rendered markup in place. */
    }
  }
}

/**
 * The discount code entry rendered by `snippets/cart-discount.liquid`, on both the drawer
 * and the cart page.
 *
 * The Cart API has no "add one code" call — `discount` replaces the whole set — so every
 * mutation here is expressed as the full set the cart should end up with. The set that is
 * already applied is read back off the rendered pills rather than a parallel attribute,
 * because the pills are themselves rendered from `cart.discount_codes`: one source of
 * truth, and it is the server's.
 *
 * A successful call re-renders every `cart-contents` on the page, replacing this element
 * wholesale with the applicability Shopify actually decided. So nothing here guesses
 * whether a code worked — it only needs `aria-busy` while a request is in flight, and a
 * client-side message for a request that never reached Shopify at all.
 */
class CartDiscount extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-cart-discount-input]');
    this.button = this.querySelector('[data-cart-discount-apply]');
    this.error = this.querySelector('[data-cart-discount-error]');
    if (!this.input || !this.button) return;

    this.button.addEventListener('click', () => this.apply());

    /* Enter applies immediately rather than waiting for a submit the field has no form
       for, mirroring the note field's own field-level Enter handling above. */
    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.apply();
      }
    });

    /* Delegated: the pills are re-rendered on every cart mutation, so a listener bound to
       the ones present at connect would die on the first change. */
    this.addEventListener('click', (event) => {
      const pill = event.target.closest('[data-discount-remove]');
      if (pill) this.remove(pill);
    });
  }

  /** Codes already on the cart, in the order the server listed them. */
  appliedCodes() {
    return Array.from(this.querySelectorAll('[data-discount-remove]')).map((pill) =>
      pill.getAttribute('data-discount-remove')
    );
  }

  async apply() {
    const code = this.input.value.trim();
    if (!code || this.busy) return;

    const applied = this.appliedCodes();
    /* Re-sending a code the cart already carries would spend a round trip to be told
       what the pill above the field is already saying. */
    if (applied.includes(code)) {
      this.input.value = '';
      return;
    }

    this.hideError();
    await this.submit(applied.concat(code), this.button);
  }

  async remove(pill) {
    if (this.busy) return;
    const code = pill.getAttribute('data-discount-remove');
    this.hideError();
    await this.submit(
      this.appliedCodes().filter((applied) => applied !== code),
      pill
    );
  }

  /**
   * `control` is whichever element the shopper pressed, so the busy state appears on the
   * thing they touched rather than always on the Apply button.
   */
  async submit(codes, control) {
    this.busy = true;
    control.setAttribute('aria-busy', 'true');
    try {
      await window.Hubcart.cart.updateDiscount(codes.join(','));
      /* On success the section re-render already replaced this element, so there is
         nothing left here to update — `finally` below runs on a detached node. */
    } catch (error) {
      this.showError(error?.data?.description || error?.message);
    } finally {
      this.busy = false;
      control.removeAttribute('aria-busy');
    }
  }

  showError(message) {
    if (!this.error) return;
    this.error.textContent = message || this.error.dataset.errorFallback || '';
    this.error.hidden = false;
  }

  hideError() {
    if (this.error) this.error.hidden = true;
  }
}

if (!customElements.get('cart-quantity')) customElements.define('cart-quantity', CartQuantity);
if (!customElements.get('cart-contents')) customElements.define('cart-contents', CartContents);
if (!customElements.get('cart-discount')) customElements.define('cart-discount', CartDiscount);

/* Two decisions per mutation: what to render from, and whether the drawer opens.
 *
 * Render: `sections` is the map the Cart API returned for every `cart-contents` that
 * registered itself, so it is used directly when present. Without it there is nothing to
 * render from yet — `component-product.js` posts to /cart/add.js itself and announces
 * plain cart data — so each element fetches its own fresh markup (see `refresh()`).
 *
 * Open: only an add opens the drawer. `source` says so outright rather than being
 * inferred from the payload, because a quantity change made *inside* the drawer also
 * carries sections and must not re-open a panel the shopper is already looking at, or
 * one they closed on purpose. An event with no `source` at all came from a publisher
 * outside the shared layer, and the only ones that exist are add paths.
 *
 * When the markup has to be fetched, the drawer opens after that settles. Opening first
 * would show whatever was in the DOM at page load — typically the empty state — for the
 * moment the fetch is in flight, which is wrong for a panel whose entire purpose is
 * confirming what just went in. */
document.addEventListener('hubcart:cart:updated', (event) => {
  const elements = Array.from(document.querySelectorAll('cart-contents'));
  const added = event.detail.source === 'add' || event.detail.source === undefined;

  const openDrawer = () => {
    if (!added) return;
    const drawer = document.getElementById('CartDrawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  };

  if (event.detail.sections) {
    elements.forEach((el) => el.render(event.detail.sections));
    openDrawer();
    return;
  }

  Promise.all(elements.map((el) => el.refresh())).finally(openDrawer);
});
